import React, {
  useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Eye as EyeIcon,
  ChevronDown as ChevronDownIcon,
  X as XIcon,
  Filter as FilterIcon,
  Play as PlayIcon,
  Sparkles as SparklesIcon,
  RotateCcw as RotateCcwIcon,
  Check as CheckIcon,
  ChevronUp as ChevronUpIcon,
  LayoutGrid as LayoutGridIcon,
  LayoutList as LayoutListIcon,
  Cpu as CpuIcon,
  UserCheck as UserCheckIcon,
  AlertTriangle as AlertTriangleIcon,
  Plus as PlusIcon,
  ChevronRight as ChevronRightIcon,
  ImageIcon as ImageIconIcon,
  Pencil as PencilIcon,
  CheckCircle2 as CheckCircle2Icon,
  GripVertical as GripVerticalIcon,
  Save as SaveIcon,
  FileDown as FileDownIcon,
} from 'lucide-react';

// Cast Lucide icons to avoid "ReactNode is not a valid JSX element" errors (project TS config)
type LucideProps = { size?: number; className?: string; 'aria-hidden'?: boolean };
const Eye = EyeIcon as React.FC<LucideProps>;
const ChevronDown = ChevronDownIcon as React.FC<LucideProps>;
const X = XIcon as React.FC<LucideProps>;
const Filter = FilterIcon as React.FC<LucideProps>;
const Play = PlayIcon as React.FC<LucideProps>;
const Sparkles = SparklesIcon as React.FC<LucideProps>;
const RotateCcw = RotateCcwIcon as React.FC<LucideProps>;
const Check = CheckIcon as React.FC<LucideProps>;
const ChevronUp = ChevronUpIcon as React.FC<LucideProps>;
const LayoutGrid = LayoutGridIcon as React.FC<LucideProps>;
const LayoutList = LayoutListIcon as React.FC<LucideProps>;
const Cpu = CpuIcon as React.FC<LucideProps>;
const UserCheck = UserCheckIcon as React.FC<LucideProps>;
const AlertTriangle = AlertTriangleIcon as React.FC<LucideProps>;
const Plus = PlusIcon as React.FC<LucideProps>;
const ChevronRight = ChevronRightIcon as React.FC<LucideProps>;
const ImageIcon = ImageIconIcon as React.FC<LucideProps>;
const Pencil = PencilIcon as React.FC<LucideProps>;
const CheckCircle2 = CheckCircle2Icon as React.FC<LucideProps>;
const GripVertical = GripVerticalIcon as React.FC<LucideProps>;
const Save = SaveIcon as React.FC<LucideProps>;
const FileDown = FileDownIcon as React.FC<LucideProps>;
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getPlaneConfig, view_groupe, QUALITY_FLAGS,
} from '../../../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';
import {
  fetchFrameImage,
  useGetGeneralViewerInstances,
  useGetInstanceFramesMetadata,
  useAssignUserView,
  useGetViewDefinitions,
  useGetViewThumbnailUrls,
  useGetViewsOrder,
  saveViewsOrder,
  useSaveSegmentations,
  type SegmentationInput,
  type SaveSegmentationsRequest,
  type GeneralViewerInstance,
} from '../../../helpers/api/view_frame/view-frames';
import type { DicomMetadataPanelOpenPayload } from '../GeneralViewer/GeneralViewerDicomMetadataPanel';
import { ViewerToolCluster } from '../GeneralViewer/ViewerToolCluster';
import {
  downloadViewFramesAsJpegZip,
  downloadViewFramesAsPngZip,
  downloadViewReferencedDicomInstances,
} from '../GeneralViewer/viewerExportUtils';
import { useQueryClient } from '@tanstack/react-query';
import { DrawingCanvas } from '../../ComponnentsUI/DrawingCanvas/components/DrawingCanvas';
import CollaborationDiscussButton from '../Collaboration/CollaborationDiscussButton';
import { buildViewFrameTarget } from '../Collaboration/collaboration-types';

// ── Quality helpers ─────────────────────────────────────────────────────────

/** Derive an effective quality score (0-100) from available data */
function effectiveQuality(view: any): number | null {
  if (view.quality_score != null) return view.quality_score as number;
  // Fallback: estimate from detection status when no criteria data
  if (view.has_user) return 100;
  if (view.has_ai && !view.has_ai_filtered) return 70;
  if (view.has_ai_filtered) return 25;
  return null;
}

function getQualityColor(score: number | null | undefined): string {
  if (score == null) return '#94a3b8';
  if (score === 0) return '#ef4444';
  if (score < 34) return '#f97316';
  if (score < 67) return '#f59e0b';
  if (score < 90) return '#84cc16';
  return '#22c55e';
}

function getQualityLabel(
  t: (key: string, opts?: any) => string,
  score: number | null | undefined,
  estimated = false
): string {
  if (score == null) return t('quality.noData');
  const suffix = estimated ? t('quality.estimatedSuffix') : '';
  if (score === 0) return t('quality.noCriteriaMet', { suffix });
  if (score < 34) return t('quality.poor', { suffix });
  if (score < 67) return t('quality.partial', { suffix });
  if (score < 90) return t('quality.good', { suffix });
  return t('quality.allCriteriaMet', { suffix });
}

function normalizeQualFlagCode(raw: string): string {
  return String(raw ?? '')
    .replace(/^QUAL_FLAG__/, '')
    .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function getViewQualityStatus(view: any): 'user' | 'ai_good' | 'ai_filtered' | 'none' {
  if (!view.exists) return 'none';
  if (view.has_user) return 'user';
  if (view.has_ai) return 'ai_good';
  if (view.has_ai_filtered) return 'ai_filtered';
  return 'ai_good';
}

// ── Lazy thumbnail (IntersectionObserver-based) ─────────────────────────────

interface LazyThumbProps {
  imageUrl: string | null;
  fallbackSrc?: string;
  alt: string;
  className?: string;
  /* If true, fetch immediately without waiting for intersection */
  eager?: boolean;
}

const LazyThumb: React.FC<LazyThumbProps> = ({ imageUrl, fallbackSrc, alt, className = '', eager }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  const doFetch = useCallback(() => {
    if (fetchedRef.current || !imageUrl) return;
    fetchedRef.current = true;
    setLoading(true);
    const ctrl = new AbortController();
    fetchFrameImage(imageUrl, { signal: ctrl.signal })
      .then((url) => { setBlobUrl(url); setLoading(false); })
      .catch((e) => { if (e?.name !== 'AbortError') setLoading(false); });
  }, [imageUrl]);

  useEffect(() => {
    fetchedRef.current = false;
    setBlobUrl(null);
    setLoading(false);
    if (!imageUrl) return;
    if (eager) { doFetch(); return; }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { doFetch(); observer.disconnect(); } },
      { rootMargin: '300px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [imageUrl, eager, doFetch]);

  return (
    // className controls positioning (e.g. "absolute inset-0 w-full h-full")
    // We add overflow-hidden so the img stays clipped to the container
    <div ref={ref} className={`overflow-hidden ${className}`}>
      {/* Fallback SVG — always shown behind the real frame */}
      {fallbackSrc && !blobUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          <img src={fallbackSrc} alt={alt} className="max-w-[85%] max-h-[85%] object-contain opacity-70" />
        </div>
      )}
      {!fallbackSrc && !blobUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
          <ImageIcon className="w-8 h-8 text-slate-300 dark:text-slate-600" />
        </div>
      )}
      {/* Spinner while loading */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-6 h-6 border-2 border-blue-400/70 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {/* Real frame — renders on top once loaded */}
      {blobUrl && (
        <img src={blobUrl} alt={alt} className="absolute inset-0 w-full h-full object-cover" />
      )}
    </div>
  );
};

// ── Shape → SegmentationInput conversion ────────────────────────────────────

function shapesToSegmentations(shapes: any[]): SegmentationInput[] {
  const result: SegmentationInput[] = [];
  for (const s of shapes) {
    const gType = (s.geometry_type ?? s.type ?? 'FREEHAND').toString().toUpperCase();
    let pts = '';
    if (s.type === 'line' || s.type === 'ruler') {
      if (s.startPoint && s.endPoint)
        pts = `${s.startPoint.x}\\${s.startPoint.y},${s.endPoint.x}\\${s.endPoint.y}`;
    } else if (s.type === 'circle' && s.center) {
      const { center: c, radiusX: rx, radiusY: ry } = s;
      const rot = s.rotation ?? 0, cos = Math.cos(rot), sin = Math.sin(rot);
      pts = [
        `${c.x + rx * cos}\\${c.y + rx * sin}`, `${c.x - rx * cos}\\${c.y - rx * sin}`,
        `${c.x - ry * sin}\\${c.y + ry * cos}`, `${c.x + ry * sin}\\${c.y - ry * cos}`,
      ].join(',');
    } else if (s.type === 'square' && s.startPoint && s.endPoint) {
      const { startPoint: sp, endPoint: ep } = s;
      const x1 = Math.min(sp.x, ep.x), x2 = Math.max(sp.x, ep.x);
      const y1 = Math.min(sp.y, ep.y), y2 = Math.max(sp.y, ep.y);
      pts = `${x1}\\${y1},${x2}\\${y1},${x2}\\${y2},${x1}\\${y2}`;
    } else if (s.type === 'freehand' && Array.isArray(s.points)) {
      pts = s.points.map((p: any) => `${p.x}\\${p.y}`).join(',');
    }
    if (!pts) continue;
    result.push({
      id: s.dbId != null ? String(s.dbId) : undefined,
      category_id: s.categoryId ?? undefined,
      label: s.label ?? undefined,
      geometry_type: gType,
      segment_type: (s.label ?? '').startsWith('MEAS__') ? 'measurement' : 'structure',
      points: pts,
      score: s.score ?? 1,
    });
  }
  return result;
}

// ── AssignToViewModal ────────────────────────────────────────────────────────

interface AssignModalProps {
  view: any;
  studyUID: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignToViewModal: React.FC<AssignModalProps> = ({ view, studyUID, onClose, onSuccess }) => {
  const { t } = useTranslation(['MedicalViewsGrid', 'GeneralViewer']);
  // Step 1 = pick frame, Step 2 = draw & validate
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedInstance, setSelectedInstance] = useState<any>(null);
  const [selectedFrame, setSelectedFrame] = useState<any>(null);
  const [frameBlobUrl, setFrameBlobUrl] = useState<string | null>(null);
  const [drawnShapes, setDrawnShapes] = useState<any[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [instanceThumbs, setInstanceThumbs] = useState<Record<string, string>>({});

  const { data: instancesData, isLoading: loadingInstances } = useGetGeneralViewerInstances(studyUID);
  const { data: framesData, isLoading: loadingFrames } = useGetInstanceFramesMetadata(
    studyUID,
    selectedInstance?.sop_instance_uid ?? null,
    undefined,
    { enabled: !!selectedInstance }
  );
  const { data: viewDefsData } = useGetViewDefinitions();
  const { assignUserView } = useAssignUserView();
  const { saveSegmentations } = useSaveSegmentations();

  const instances: any[] = useMemo(() => {
    const raw = instancesData?.data?.data ?? instancesData?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [instancesData]);

  const frames: any[] = useMemo(
    () => framesData?.data?.data?.frames ?? framesData?.data?.frames ?? [],
    [framesData]
  );

  const viewDefId: number | undefined = useMemo(() => {
    if (view.view_def_id) return view.view_def_id;
    const defs: any[] = viewDefsData?.data?.data ?? viewDefsData?.data ?? [];
    const match = (Array.isArray(defs) ? defs : []).find((d: any) => d.view_code === view.view_code);
    return match?.id;
  }, [view, viewDefsData]);

  // Quality criteria for the view (from static planes config)
  const qualityCriteria: string[] = useMemo(() => {
    const flags = QUALITY_FLAGS[view.view_code] || [];
    return (flags as any[]).map((f: any) => {
      const normalized = normalizeQualFlagCode(f.code ?? f.name);
      return normalized ? t(`qualityFlag.${normalized}`, { ns: 'GeneralViewer', defaultValue: f.name }) : f.name;
    });
  }, [view.view_code, t]);

  const planeConfig = useMemo(() => getPlaneConfig(view.view_code), [view.view_code]);

  const viewName = useMemo(
    () => t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name }),
    [t, view.view_code, view.display_name]
  );

  // Fetch instance thumbnails eagerly on mount
  useEffect(() => {
    instances.forEach((inst: any) => {
      if (!inst.first_frame_image_url || instanceThumbs[inst.sop_instance_uid]) return;
      fetchFrameImage(inst.first_frame_image_url)
        .then((url) => setInstanceThumbs((p) => ({ ...p, [inst.sop_instance_uid]: url })))
        .catch(() => {});
    });
  }, [instances]);

  // Load blob URL for selected frame (needed for DrawingCanvas)
  useEffect(() => {
    setFrameBlobUrl(null);
    if (!selectedFrame?.image_url) return;
    fetchFrameImage(selectedFrame.image_url).then(setFrameBlobUrl).catch(() => {});
  }, [selectedFrame]);

  const handleFrameSelect = (frame: any) => {
    setSelectedFrame(frame);
    setDrawnShapes([]);
  };

  const handleGoToStep2 = () => {
    if (!selectedFrame) return;
    setStep(2);
  };

  const handleSaveAndAssign = useCallback(async () => {
    if (!selectedFrame?.frame_id || !viewDefId) return;
    setAssigning(true);
    setAssignError(null);
    try {
      // 1. Save drawn segmentations (only if shapes were drawn)
      const segs = shapesToSegmentations(drawnShapes);
      if (segs.length > 0) {
        const payload: SaveSegmentationsRequest = {
          sop_instance_uid: selectedFrame.sop_instance_uid,
          frame_number: Number(selectedFrame.frame_number),
          frame_id: selectedFrame.frame_id,
          segmentations: segs,
        };
        const saveResult = await saveSegmentations(payload);
        if (saveResult?.success === false) {
          setAssignError(saveResult.error || 'Failed to save structures');
          setAssigning(false);
          return;
        }
      }
      // 2. Assign user view
      const assignResult = await assignUserView(selectedFrame.frame_id, viewDefId, false);
      if (assignResult?.success === false || assignResult?.error) {
        setAssignError(assignResult.error || 'Failed to assign view');
      } else {
        onSuccess();
        onClose();
      }
    } catch (e: any) {
      setAssignError(e?.message || 'An error occurred');
    } finally {
      setAssigning(false);
    }
  }, [selectedFrame, viewDefId, drawnShapes, saveSegmentations, assignUserView, onSuccess, onClose]);

  const canAssign = drawnShapes.length > 0 && !!selectedFrame && !!viewDefId && !assigning;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-h-[92vh] flex flex-col overflow-hidden"
        style={{ maxWidth: step === 2 ? '1100px' : '860px' }}>

        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-3">
            {planeConfig?.image && (
              <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <img src={planeConfig.image} alt={viewName} className="w-7 h-7 object-contain" />
              </div>
            )}
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">{t('assignModal.title')}</p>
              <h2 className="text-sm font-semibold text-slate-800 dark:text-white leading-tight">{viewName}</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicator */}
            <div className="flex items-center gap-1.5 text-xs">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full font-medium ${step === 1 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-slate-300 text-slate-500'}`}>1</span>
                <span className="hidden sm:inline">{t('assignModal.steps.selectFrame')}</span>
              </div>
              <ChevronRight size={12} className="text-slate-300" />
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full font-medium ${step === 2 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-blue-500 text-white' : 'bg-slate-300 text-slate-500'}`}>2</span>
                <span className="hidden sm:inline">{t('assignModal.steps.drawValidate')}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Step 1: Instance + Frame picker */}
        {step === 1 && (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Instance list */}
            <div className="w-48 border-r border-slate-100 dark:border-slate-700 flex flex-col flex-shrink-0 bg-slate-50 dark:bg-slate-800/50">
              <div className="px-3 py-2 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide border-b border-slate-100 dark:border-slate-700">
                {loadingInstances ? 'Loading…' : `${instances.length} instance${instances.length !== 1 ? 's' : ''}`}
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingInstances ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : instances.map((inst: any, i: number) => {
                  const active = selectedInstance?.sop_instance_uid === inst.sop_instance_uid;
                  return (
                    <button key={inst.sop_instance_uid}
                      onClick={() => { setSelectedInstance(inst); setSelectedFrame(null); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-xs transition-colors ${active ? 'bg-blue-50 dark:bg-blue-900/30 border-l-2 border-l-blue-500' : 'hover:bg-white dark:hover:bg-slate-700 border-l-2 border-l-transparent'} border-b border-slate-100 dark:border-slate-700/50`}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 flex-shrink-0 relative">
                        {instanceThumbs[inst.sop_instance_uid]
                          ? <img src={instanceThumbs[inst.sop_instance_uid]} alt="" className="w-full h-full object-cover" />
                          : <ImageIcon size={14} className="absolute inset-0 m-auto text-slate-400" />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`font-semibold truncate ${active ? 'text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200'}`}>
                          {inst.format_file || 'DICOM'} #{i + 1}
                        </div>
                      <div className="text-slate-400 text-xs">{t('stats.frames', { count: inst.frame_count })}</div>
                      </div>
                      {active && <ChevronRight size={11} className="text-blue-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Frame grid */}
            <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900">
              {!selectedInstance ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400 dark:text-slate-500">
                  <ImageIcon className="w-12 h-12 mb-3 text-slate-200 dark:text-slate-700" />
                  <p className="text-sm font-medium">Select an instance</p>
                  <p className="text-xs mt-1">Choose from the list on the left to browse frames</p>
                </div>
              ) : loadingFrames ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      {frames.length} frames — click to select
                    </span>
                    {selectedFrame && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        Frame #{selectedFrame.frame_number} selected
                      </span>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-3">
                    <div className="grid grid-cols-5 gap-2">
                      {frames.map((frame: any) => {
                        const isSelected = selectedFrame?.frame_id === frame.frame_id;
                        return (
                          <button key={frame.frame_id} onClick={() => handleFrameSelect(frame)}
                            className={`relative rounded-xl overflow-hidden transition-all duration-150 group ${isSelected ? 'ring-2 ring-blue-500 ring-offset-1 shadow-lg scale-[1.03]' : 'hover:ring-2 hover:ring-slate-300 dark:hover:ring-slate-600'}`}
                          >
                            <div style={{ paddingBottom: '75%', position: 'relative' }} className="bg-slate-100 dark:bg-slate-800">
                              <LazyThumb
                                imageUrl={frame.image_url}
                                alt={`Frame ${frame.frame_number}`}
                                className="absolute inset-0"
                              />
                            </div>
                            <div className={`absolute bottom-0 left-0 right-0 py-0.5 text-center text-xs font-medium transition-colors ${isSelected ? 'bg-blue-500 text-white' : 'bg-black/40 text-white group-hover:bg-black/55'}`}>
                              #{frame.frame_number}
                            </div>
                            {isSelected && (
                              <div className="absolute top-1 right-1">
                                <CheckCircle2 size={16} className="text-blue-500 fill-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Draw & Validate */}
        {step === 2 && selectedFrame && (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Drawing area */}
            <div className="flex-1 min-w-0 flex flex-col bg-black">
              {frameBlobUrl ? (
                <DrawingCanvas
                  images={[{
                    id: selectedFrame.frame_id,
                    name: String(selectedFrame.frame_number),
                    src: frameBlobUrl,
                  }]}
                  viewCode={view.view_code}
                  showSaveButton={false}
                  onShapeAdded={(shape) => setDrawnShapes((prev) => [...prev, shape])}
                  onShapeDeleted={(shapeId) => setDrawnShapes((prev) => prev.filter((s: any) => s.id !== shapeId))}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Right panel: criteria + instructions */}
            <div className="w-64 flex-shrink-0 border-l border-slate-200 dark:border-slate-700 flex flex-col bg-white dark:bg-slate-900 overflow-hidden">
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
                <div className="flex items-center gap-2 mb-1">
                  <Pencil size={13} className="text-blue-500" />
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Draw Required Structures</p>
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Use the toolbar to annotate the visible structures. Draw at least one structure to enable saving.
                </p>
              </div>

              {/* Selected frame info */}
              <div className="p-3 border-b border-slate-100 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded overflow-hidden bg-slate-200 flex-shrink-0 relative">
                    {selectedFrame.image_url && (
                      <LazyThumb imageUrl={selectedFrame.image_url} alt="" className="absolute inset-0" eager />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Frame #{selectedFrame.frame_number}</p>
                    <p className="text-xs text-slate-400">{selectedInstance?.format_file || 'DICOM'}</p>
                  </div>
                </div>
              </div>

              {/* Quality criteria checklist */}
              {qualityCriteria.length > 0 && (
                <div className="flex-1 overflow-y-auto p-3">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    {t('assignModal.qualityCriteriaTitle')}
                  </p>
                  <div className="space-y-1.5">
                    {qualityCriteria.map((criterion, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-slate-600 dark:text-slate-300 leading-tight">{criterion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shapes drawn status */}
              <div className="p-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
                <div className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium ${drawnShapes.length > 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-800 text-slate-400'}`}>
                  {drawnShapes.length > 0
                    ? <><CheckCircle2 size={13} /> {t('assignModal.structuresDrawn', { count: drawnShapes.length })}</>
                    : <><Pencil size={13} /> {t('assignModal.noStructuresDrawn')}</>
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900">
          <div>
            {assignError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle size={12} /> {assignError}
              </p>
            )}
            {!viewDefId && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle size={12} /> {t('assignModal.viewDefinitionNotFound')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1.5 transition-colors">
                <ChevronRight size={13} className="rotate-180" /> {t('actions.back')}
              </button>
            )}
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              {t('actions.cancel')}
            </button>
            {step === 1 ? (
              <button
                onClick={handleGoToStep2}
                disabled={!selectedFrame}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${selectedFrame ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'}`}
              >
                {t('assignModal.nextDraw')} <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleSaveAndAssign}
                disabled={!canAssign}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${canAssign ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'}`}
              >
                {assigning
                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('actions.saving')}</>
                  : <><CheckCircle2 size={14} /> {t('assignModal.saveAndAssign')}</>
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Filter sub-components ─────────────────────────────────────────────────────

const CompactMultiSelect: React.FC<{ options: any[]; value: any[]; onChange: (v: any[]) => void; placeholder: string; label: string }> = ({ options, value, onChange, placeholder, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <div onClick={() => setOpen(!open)} className="min-h-[36px] px-3 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 cursor-pointer flex items-center justify-between hover:border-slate-300 transition-colors">
        <span className="text-sm text-slate-700 dark:text-slate-300">{value.length === 0 ? placeholder : `${value.length} selected`}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-30 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl max-h-56 overflow-auto">
          {options.map((opt) => {
            const sel = value.includes(opt);
            return (
              <div key={typeof opt === 'object' ? opt.code : opt} onClick={() => onChange(sel ? value.filter(v => v !== opt) : [...value, opt])}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 text-sm flex items-center gap-2 ${sel ? 'text-blue-600' : 'text-slate-700 dark:text-slate-300'}`}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white dark:bg-slate-800'}`}>
                  {sel && <Check size={10} className="text-white" />}
                </div>
                <span className="truncate">{typeof opt === 'object' ? opt.name : String(opt).replace(/_/g, ' ')}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const QualityFlagsManager: React.FC<{ options: any[]; value: any[]; onChange: (v: any[]) => void; placeholder: string; label: string }> = ({ options, value, onChange, placeholder, label }) => {
  const [open, setOpen] = useState(false);
  const [vis, setVis] = useState<Record<string, boolean>>(() => {
    const m: Record<string, boolean> = {};
    Object.values(QUALITY_FLAGS).flat().forEach((f: any) => { m[f.code] = f.shouldBeVisible; });
    return m;
  });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <div onClick={() => setOpen(!open)} className="min-h-[36px] px-3 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 cursor-pointer flex items-center justify-between hover:border-slate-300 transition-colors">
        <span className="text-sm text-slate-700 dark:text-slate-300">{value.length === 0 ? placeholder : `${value.length} selected`}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-30 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl max-h-56 overflow-auto">
          {options.map((opt) => {
            const sel = value.some(v => v.code === opt.code);
            const cv = vis[opt.code] ?? opt.shouldBeVisible;
            return (
              <div key={opt.code} onClick={() => onChange(sel ? value.filter(v => v.code !== opt.code) : [...value, { ...opt, shouldBeVisible: cv }])}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 text-sm flex items-center justify-between ${sel ? 'text-blue-600' : 'text-slate-700 dark:text-slate-300'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white dark:bg-slate-800'}`}>
                    {sel && <Check size={10} className="text-white" />}
                  </div>
                  <span className="truncate text-xs">{opt.name}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); const nv = !cv; setVis(p => ({ ...p, [opt.code]: nv })); onChange(value.map(i => i.code === opt.code ? { ...i, shouldBeVisible: nv } : i)); }}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${cv ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {cv ? <Check size={9} /> : <X size={9} />}
                  {cv ? 'Visible' : 'Hidden'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Per-view download (grid + list) ─────────────────────────────────────────

function ViewDownloadMenu({
  studyUID,
  viewCode,
  frameCount,
  dicomInstances,
}: {
  studyUID: string;
  viewCode: string;
  frameCount: number;
  dicomInstances: GeneralViewerInstance[];
}) {
  const { t } = useTranslation('GeneralViewer');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const [menuFixedStyle, setMenuFixedStyle] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    width: number;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = 280;
    const gutter = 8;
    let left = rect.right - menuWidth;
    left = Math.max(gutter, Math.min(left, window.innerWidth - menuWidth - gutter));
    const belowTop = rect.bottom + 6;
    const spaceBelow = window.innerHeight - belowTop - gutter;
    const menuMin = 120;
    let top = belowTop;
    if (spaceBelow < menuMin) {
      const aboveTop = rect.top - menuMin - 6;
      if (aboveTop >= gutter) {
        top = aboveTop;
      }
    }
    const maxHeight = Math.max(menuMin, window.innerHeight - top - gutter);
    setMenuFixedStyle({ top, left, maxHeight, width: menuWidth });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuFixedStyle(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const node = e.target as Node;
      if (wrapRef.current?.contains(node)) return;
      if (menuPortalRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemCls =
    'w-full text-left px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg';

  const n = Math.max(1, frameCount);

  const menuShell =
    'min-w-[248px] rounded-xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-900 shadow-xl py-1.5 overflow-y-auto overflow-x-hidden';

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-white/95 dark:bg-slate-800/95 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm"
        title={t('export.menu')}
        aria-expanded={open}
      >
        <FileDown size={12} />
        <span className="hidden sm:inline">{t('toolbar.exportShort')}</span>
        <ChevronDown size={11} className={`opacity-70 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open &&
        menuFixedStyle &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuPortalRef}
            className={menuShell}
            style={{
              position: 'fixed',
              top: menuFixedStyle.top,
              left: menuFixedStyle.left,
              width: menuFixedStyle.width,
              maxHeight: menuFixedStyle.maxHeight,
              zIndex: 10050,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={itemCls}
              onClick={() => {
                setOpen(false);
                void downloadViewFramesAsJpegZip(studyUID, viewCode, n, t);
              }}
            >
              {t('export.viewFramesJpegZip')}
            </button>
            <button
              type="button"
              className={itemCls}
              onClick={() => {
                setOpen(false);
                void downloadViewFramesAsPngZip(studyUID, viewCode, n, t);
              }}
            >
              {t('export.viewFramesPngZip')}
            </button>
            <button
              type="button"
              className={itemCls}
              onClick={() => {
                setOpen(false);
                void downloadViewReferencedDicomInstances(studyUID, viewCode, n, dicomInstances, t);
              }}
            >
              {t('export.viewAllDicom')}
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}

// ── ViewCard (Grid) ───────────────────────────────────────────────────────────

const GROUP_COLORS: Record<string, string> = {
  CARDIAC: '#ef4444', ABDOMINAL_GENITALIA: '#f97316',
  FACIAL: '#06b6d4', SPINAL: '#6366f1',
};

interface ViewCardProps {
  view: any;
  viewGrouping: any;
  onViewSelect: (v: any, initialFrameId?: string) => void;
  onAssign: (v: any) => void;
  isMagicActive: boolean;
  studyUID?: string;
  dicomInstances?: GeneralViewerInstance[];
  isReordering?: boolean;
}

const ViewCard: React.FC<ViewCardProps> = ({
  view,
  viewGrouping,
  onViewSelect,
  onAssign,
  isMagicActive,
  studyUID,
  dicomInstances = [],
  isReordering = false,
}) => {
  const { t } = useTranslation(['MedicalViewsGrid', 'GeneralViewer']);
  const planeConfig = useMemo(() => getPlaneConfig(view.view_code), [view.view_code]);
  const isExisting = view.exists;
  const qualityScore = view.quality_score as number | null;
  const total = view.quality_criteria_total as number ?? 0;
  const met = view.quality_criteria_met as number ?? 0;
  const effScore = effectiveQuality(view);
  const isEstimated = qualityScore == null && effScore != null;
  const qColor = getQualityColor(effScore);
  const viewLabel = t(`viewTag.${view.view_code}`, {
    ns: 'GeneralViewer',
    defaultValue: view.display_name,
  });
  const discussTarget =
    isExisting && view.primary_frame_id
      ? buildViewFrameTarget(viewLabel, view.primary_frame_id, view.first_frame_number, view.view_code)
      : null;

  return (
    <div
      className={`h-full rounded-2xl overflow-hidden flex flex-col transition-all duration-200 group cursor-pointer
        ${isExisting
          ? 'bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5'
          : 'bg-slate-50 dark:bg-slate-800/50 opacity-60 hover:opacity-80'
        }`}
      style={{ border: isExisting ? `1.5px solid ${qColor}30` : '1.5px solid #e2e8f0' }}
      onClick={() => isExisting ? onViewSelect(view) : onAssign(view)}
    >
      {/* Thumbnail area */}
      <div className="relative overflow-hidden" style={{ height: '176px' }}>
        {/* Quality accent bar (left edge) */}
        <div className="absolute left-0 top-0 bottom-0 w-1 z-10 rounded-tr rounded-br"
          style={{ background: isExisting ? qColor : '#cbd5e1' }} />

        {/* Main thumbnail */}
        {isExisting ? (
          <LazyThumb
            imageUrl={view.first_frame_image_url}
            fallbackSrc={planeConfig?.image}
            alt={t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })}
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-700">
            {planeConfig?.image
              ? <img src={planeConfig.image} alt={t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })} className="max-w-full max-h-full object-contain p-4 opacity-70" />
              : <Eye className="w-10 h-10 text-slate-200 dark:text-slate-600" />
            }
          </div>
        )}

        {/* Gradient overlay at bottom */}
        {isExisting && (
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent z-10" />
        )}

        {/* Top badges */}
        <div className="absolute top-2 left-3 flex gap-1 z-20">
          {isExisting && view.has_user && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-500 text-white shadow-sm">
              <UserCheck size={9} /> {t('badges.you')}
            </span>
          )}
          {isExisting && view.has_ai && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-semibold bg-blue-500 text-white shadow-sm">
              <Cpu size={9} /> AI
            </span>
          )}
          {isExisting && !view.has_ai && view.has_ai_filtered && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-semibold bg-amber-400 text-white shadow-sm">
              <AlertTriangle size={9} /> {t('badges.lowQa')}
            </span>
          )}
        </div>

        {/* Top-right: export + frame count */}
        <div className="absolute top-2 right-2 z-20 flex flex-col items-end gap-1">
          {isExisting &&
            studyUID &&
            dicomInstances.length > 0 &&
            view.frame_count > 0 &&
            !isReordering && (
              <ViewDownloadMenu
                studyUID={studyUID}
                viewCode={view.view_code}
                frameCount={view.frame_count}
                dicomInstances={dicomInstances}
              />
            )}
          {isExisting && view.frame_count > 0 ? (
            <span className="px-1.5 py-0.5 rounded-md text-xs font-medium bg-black/40 text-white backdrop-blur-sm">
              {t('badges.frameCount', { count: view.frame_count })}
            </span>
          ) : !isExisting ? (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-medium bg-slate-700/80 text-white">
              <X size={9} /> {t('badges.na')}
            </span>
          ) : null}
        </div>

        {/* Magic badge */}
        {isMagicActive && isExisting && (
          <div className="absolute top-8 right-2 z-20 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-purple-600 shadow-sm">
            <Sparkles size={10} /> {t('badges.magic')}
          </div>
        )}

        {/* Anatomical SVG watermark bottom-right */}
        {planeConfig?.image && isExisting && (
          <div className="absolute bottom-1 right-2 z-20 opacity-80">
            <img src={planeConfig.image} alt="" className="w-10 h-10 object-contain drop-shadow-sm" />
          </div>
        )}

        {/* View name overlay at bottom */}
        {isExisting && (
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-4 z-20">
            <p className="text-white text-xs font-semibold truncate leading-tight drop-shadow-sm">
              {t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })}
            </p>
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="px-3 pt-2.5 pb-2 flex flex-col gap-1.5">
        {!isExisting && (
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 truncate">
            {t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })}
          </p>
        )}

        {/* Group + family badges */}
        <div className="flex flex-wrap items-center gap-1">
          {viewGrouping && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
              {t(`group.${viewGrouping.group}`, { defaultValue: viewGrouping.group.replace(/_/g, ' ') })}
            </span>
          )}
          {viewGrouping?.family && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
              {t(`family.${viewGrouping.family}`, { defaultValue: viewGrouping.family })}
            </span>
          )}
        </div>

        {/* Quality progress bar */}
        {isExisting && (
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-xs truncate" style={{ color: effScore != null ? qColor : '#94a3b8' }}>
                {getQualityLabel(t, effScore, isEstimated)}
              </span>
              <span className="text-xs font-bold ml-1 shrink-0" style={{ color: effScore != null ? qColor : '#94a3b8' }}>
                {qualityScore != null && total > 0
                  ? `${met}/${total}`
                  : effScore != null
                    ? `${effScore}%`
                    : '—'}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${effScore ?? 0}%`, background: effScore != null ? qColor : '#cbd5e1' }} />
            </div>
          </div>
        )}
      </div>

      {/* Action */}
      <div className={`px-3 py-2 border-t mt-auto ${isExisting ? 'border-slate-100 dark:border-slate-700' : 'border-slate-200 dark:border-slate-700'}`}>
        {isExisting ? (
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); onViewSelect(view); }}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-1 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <Eye size={12} /> {t('actions.viewDetails')}
            </button>
            {discussTarget && (
              <CollaborationDiscussButton target={discussTarget} variant="card" />
            )}
          </div>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); onAssign(view); }}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-1 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
            <Plus size={12} /> {t('actions.assignImage')}
          </button>
        )}
      </div>
    </div>
  );
};

// ── ViewListRow (List mode) ───────────────────────────────────────────────────

const ViewListRow: React.FC<{
  view: any;
  viewGrouping: any;
  studyUID?: string;
  dicomInstances?: GeneralViewerInstance[];
  onViewSelect: (v: any, initialFrameId?: string) => void;
  onAssign: (v: any) => void;
}> = ({ view, viewGrouping, studyUID, dicomInstances = [], onViewSelect, onAssign }) => {
  const { t } = useTranslation(['MedicalViewsGrid', 'GeneralViewer']);
  const planeConfig = useMemo(() => getPlaneConfig(view.view_code), [view.view_code]);
  const isExisting = view.exists;
  const effScore = effectiveQuality(view);
  const isEstimated = (view.quality_score == null) && effScore != null;
  const qColor = getQualityColor(effScore);
  const [expanded, setExpanded] = useState(false);
  const viewLabel = t(`viewTag.${view.view_code}`, {
    ns: 'GeneralViewer',
    defaultValue: view.display_name,
  });
  const discussTarget =
    isExisting && view.primary_frame_id
      ? buildViewFrameTarget(viewLabel, view.primary_frame_id, view.first_frame_number, view.view_code)
      : null;

  const allFrameCount = (view.frame_count as number) || 200;

  const { data: thumbsData, isLoading: loadingThumbs } = useGetViewThumbnailUrls(
    studyUID ?? null,
    view.view_code,
    allFrameCount,
    { enabled: expanded && isExisting }
  );
  const thumbUrls: { thumbnail_url: string; frame_number: number; met_criteria: number }[] = useMemo(
    () => thumbsData?.data?.data ?? thumbsData?.data ?? [],
    [thumbsData]
  );

  const handleRowClick = () => {
    if (isExisting) setExpanded(prev => !prev);
    else onAssign(view);
  };

  return (
    <div className={`border-b border-slate-100 dark:border-slate-700 transition-colors ${isExisting ? '' : 'opacity-55'}`}>
      {/* Main row */}
      <div
        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${isExisting ? 'hover:bg-slate-50 dark:hover:bg-slate-700/40' : ''}`}
        onClick={handleRowClick}
      >
        {/* Quality accent bar */}
        <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: isExisting ? qColor : '#e2e8f0', minHeight: '36px' }} />

        {/* Thumbnail */}
        <div className="relative w-14 h-11 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200 dark:border-slate-600">
          {isExisting ? (
            <LazyThumb
              imageUrl={view.first_frame_image_url}
              fallbackSrc={planeConfig?.image}
              alt={t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })}
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
              {planeConfig?.image
                ? <img src={planeConfig.image} alt="" className="w-full h-full object-contain p-1 opacity-70" />
                : <Eye size={16} className="text-slate-300" />
              }
            </div>
          )}
          {planeConfig?.image && isExisting && (
            <div className="absolute bottom-0 right-0 opacity-80">
              <img src={planeConfig.image} alt="" className="w-5 h-5 object-contain drop-shadow-sm" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`font-semibold text-sm truncate ${isExisting ? 'text-slate-800 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>
              {t(`viewTag.${view.view_code}`, { ns: 'GeneralViewer', defaultValue: view.display_name })}
            </span>
            {isExisting && view.has_user && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex-shrink-0"><UserCheck size={9} /> {t('badges.you')}</span>}
            {isExisting && view.has_ai && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex-shrink-0"><Cpu size={9} /> AI</span>}
            {isExisting && !view.has_ai && view.has_ai_filtered && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 flex-shrink-0"><AlertTriangle size={9} /> {t('badges.lowQa')}</span>}
          </div>
          <div className="flex items-center flex-wrap gap-1 mt-0.5">
            {viewGrouping?.group && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
                {t(`group.${viewGrouping.group}`, { defaultValue: viewGrouping.group.replace(/_/g, ' ') })}
              </span>
            )}
            {viewGrouping?.family && (
              <span className="text-xs px-1.5 py-0.5 rounded text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
                {t(`family.${viewGrouping.family}`, { defaultValue: viewGrouping.family })}
              </span>
            )}
            {isExisting && view.frame_count > 0 && (
              <span className="text-xs text-slate-400 ml-0.5">{t('stats.frames', { count: view.frame_count })}</span>
            )}
          </div>
        </div>

        {/* Quality bar */}
        {isExisting && (
          <div className="hidden md:flex flex-col items-end gap-0.5 w-24 flex-shrink-0">
            <span className="text-xs font-semibold" style={{ color: effScore != null ? qColor : '#94a3b8' }}>
              {effScore != null
                ? (view.quality_criteria_total > 0 ? `${view.quality_criteria_met}/${view.quality_criteria_total}` : `${effScore}%`)
                : '—'}
              {isEstimated && <span className="opacity-60 ml-0.5 text-[10px]">est.</span>}
            </span>
            <div className="w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${effScore ?? 0}%`, background: effScore != null ? qColor : '#cbd5e1' }} />
            </div>
            <span className="text-[10px] text-slate-400 truncate w-full text-right">
              {getQualityLabel(t, effScore, isEstimated)}
            </span>
          </div>
        )}

        {/* Expand chevron / action */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {isExisting &&
            studyUID &&
            dicomInstances.length > 0 &&
            view.frame_count > 0 && (
              <div onClick={(e) => e.stopPropagation()}>
                <ViewDownloadMenu
                  studyUID={studyUID}
                  viewCode={view.view_code}
                  frameCount={view.frame_count}
                  dicomInstances={dicomInstances}
                />
              </div>
            )}
          {isExisting ? (
            <>
              {discussTarget && (
                <CollaborationDiscussButton target={discussTarget} variant="card" />
              )}
              <button onClick={(e) => { e.stopPropagation(); onViewSelect(view); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                <Eye size={12} /> Details
              </button>
              <span className={`transition-transform duration-200 text-slate-400 ${expanded ? 'rotate-180' : ''}`}>
                <ChevronDown size={16} />
              </span>
            </>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onAssign(view); }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <Plus size={12} /> Assign
            </button>
          )}
        </div>
      </div>

      {/* Expanded frames panel */}
      {expanded && isExisting && (
        <div className="px-4 pb-4 bg-slate-50 dark:bg-slate-800/50">
          {loadingThumbs ? (
            <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
              <div className="w-4 h-4 border-2 border-blue-400/70 border-t-transparent rounded-full animate-spin" />
              Loading frames…
            </div>
          ) : thumbUrls.length === 0 ? (
            <p className="text-xs text-slate-400 py-3">No frames available.</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1 pt-2">
              {thumbUrls.map((t, i) => (
                <div
                  key={i}
                  className="relative flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all group"
                  onClick={() => onViewSelect(view, t.frame_id)}
                  title={`Frame ${t.frame_number} · ${t.met_criteria} criteria met`}
                >
                  <LazyThumb
                    imageUrl={t.thumbnail_url}
                    fallbackSrc={planeConfig?.image}
                    alt={`Frame ${t.frame_number}`}
                    className="absolute inset-0 w-full h-full"
                    eager
                  />
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/50 text-[10px] text-white text-center">
                    #{t.frame_number}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sortable wrappers ────────────────────────────────────────────────────────

function SortableViewCard({ view, viewGrouping, studyUID, dicomInstances, onViewSelect, onAssign, isMagicActive, isReordering }: {
  view: any;
  viewGrouping: any;
  studyUID?: string;
  dicomInstances: GeneralViewerInstance[];
  onViewSelect: any;
  onAssign: any;
  isMagicActive: boolean;
  isReordering: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: view.view_code });
  return (
    <div
      ref={setNodeRef}
      {...(isReordering ? { ...listeners, ...attributes } : {})}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }}
      className={`h-full ${isReordering ? 'cursor-grab active:cursor-grabbing touch-none select-none' : ''}`}
    >
      {/* Reorder-mode overlay: tinted border + badge, no grip icon cluttering the card */}
      <div className={`h-full flex flex-col ${isReordering ? 'relative ring-2 ring-violet-400/70 rounded-2xl' : ''}`}>
        {isReordering && (
          <div className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded-md bg-violet-500/80 backdrop-blur-sm text-white text-[9px] font-bold pointer-events-none">
            drag
          </div>
        )}
        <ViewCard
          view={view}
          viewGrouping={viewGrouping}
          onViewSelect={isReordering ? () => {} : onViewSelect}
          onAssign={isReordering ? () => {} : onAssign}
          isMagicActive={isMagicActive}
          studyUID={studyUID}
          dicomInstances={dicomInstances}
          isReordering={isReordering}
        />
      </div>
    </div>
  );
}

function SortableViewListRow({ view, viewGrouping, studyUID, dicomInstances, onViewSelect, onAssign, isReordering }: {
  view: any;
  viewGrouping: any;
  studyUID?: string;
  dicomInstances: GeneralViewerInstance[];
  onViewSelect: any;
  onAssign: any;
  isReordering: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: view.view_code });
  return (
    <div
      ref={setNodeRef}
      {...(isReordering ? { ...listeners, ...attributes } : {})}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }}
      className={isReordering ? 'cursor-grab active:cursor-grabbing touch-none select-none ring-1 ring-inset ring-violet-300 dark:ring-violet-700' : ''}
    >
      <ViewListRow
        view={view}
        viewGrouping={viewGrouping}
        studyUID={studyUID}
        dicomInstances={dicomInstances}
        onViewSelect={isReordering ? () => {} : onViewSelect}
        onAssign={isReordering ? () => {} : onAssign}
      />
    </div>
  );
}

// ── MedicalViewsGrid (Main) ───────────────────────────────────────────────────

interface MedicalViewsGridProps {
  reportViews: any;
  onViewSelect: (view: any, views: any[], index: number, appliedFilters?: any, initialFrameId?: string) => void;
  availableSeries: any[];
  selectedSerieType: string[];
  onSerieFilterChange: (serieId: string) => void;
  onClearAllSeries: () => void;
  studyUID?: string;
  onViewDataChange?: () => void;
  /** Called instead of opening the modal when "Assign Image" is clicked — navigates to General Viewer. */
  onAssignInGeneralViewer?: (view: any) => void;
  /** Study instances for export / DICOM (same query as AiAccelerator parent). */
  dicomInstances?: GeneralViewerInstance[];
  onOpenDicomMetadata?: (payload: DicomMetadataPanelOpenPayload) => void;
}

const MedicalViewsGrid: React.FC<MedicalViewsGridProps> = ({
  reportViews, onViewSelect, availableSeries, selectedSerieType,
  onSerieFilterChange, onClearAllSeries, studyUID, onViewDataChange, onAssignInGeneralViewer,
  dicomInstances = [],
  onOpenDicomMetadata,
}) => {
  const [displayMode, setDisplayMode] = useState<'grid' | 'list'>(
    () => (localStorage.getItem('views-display-mode') as 'grid' | 'list') ?? 'grid'
  );
  const setDisplayModeAndSave = (mode: 'grid' | 'list') => {
    localStorage.setItem('views-display-mode', mode);
    setDisplayMode(mode);
  };
  const [filterMode] = useState<'view' | 'quality'>('view');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedViews, setSelectedViews] = useState<string[]>([]);
  const [selectedQualityFlags, setSelectedQualityFlags] = useState<any[]>([]);
  const [magicFilterActive, setMagicFilterActive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isSeriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  // assigningView is only used as fallback when onAssignInGeneralViewer is not provided
  const [assigningView, setAssigningView] = useState<any>(null);
  const handleAssign = (view: any) => {
    if (onAssignInGeneralViewer) {
      onAssignInGeneralViewer(view);
    } else {
      setAssigningView(view);
    }
  };

  // ── Reorder state ────────────────────────────────────────────────────────────
  const [isReordering, setIsReordering] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [orderSaved, setOrderSaved] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const queryClient = useQueryClient();

  // Load saved order from DB; fall back to localStorage while the request is in-flight
  const { data: savedOrderData } = useGetViewsOrder(studyUID);
  const dbOrder: string[] = savedOrderData?.data?.data?.order ?? savedOrderData?.data?.order ?? [];

  // Local working copy — updated immediately on drag, persisted to DB on Save
  const [customOrder, setCustomOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(`views-order-${studyUID}`) || 'null') || []; } catch { return []; }
  });

  // Once DB data arrives, prefer it over the localStorage cache
  const prevDbOrder = useRef<string[]>([]);
  useEffect(() => {
    if (dbOrder.length > 0 && JSON.stringify(dbOrder) !== JSON.stringify(prevDbOrder.current)) {
      prevDbOrder.current = dbOrder;
      setCustomOrder(dbOrder);
      // Keep localStorage in sync as an offline fallback
      localStorage.setItem(`views-order-${studyUID}`, JSON.stringify(dbOrder));
    }
  }, [dbOrder, studyUID]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (e: DragStartEvent) => setActiveDragId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setCustomOrder((prev) => {
      const base = prev.length > 0 ? prev : orderedViews.map(v => v.view_code);
      const oldIdx = base.indexOf(String(active.id));
      const newIdx = base.indexOf(String(over.id));
      if (oldIdx === -1 || newIdx === -1) return base;
      return arrayMove(base, oldIdx, newIdx);
    });
    setOrderSaved(false);
  };

  const saveOrder = async () => {
    const orderToSave = customOrder.length > 0 ? customOrder : orderedViews.map(v => v.view_code);
    setIsSavingOrder(true);
    try {
      await saveViewsOrder(studyUID!, orderToSave);
      // Update local cache immediately
      localStorage.setItem(`views-order-${studyUID}`, JSON.stringify(orderToSave));
      setCustomOrder(orderToSave);
      queryClient.setQueryData(['views-order', studyUID], (old: any) => ({
        ...old,
        data: { ...(old?.data ?? {}), order: orderToSave },
      }));
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 2500);
    } catch {
      // Silent — order is still in localStorage as fallback
    } finally {
      setIsSavingOrder(false);
    }
  };

  const resetOrder = async () => {
    setIsSavingOrder(true);
    try {
      await saveViewsOrder(studyUID!, []);
      localStorage.removeItem(`views-order-${studyUID}`);
      setCustomOrder([]);
      queryClient.setQueryData(['views-order', studyUID], (old: any) => ({
        ...old,
        data: { ...(old?.data ?? {}), order: [] },
      }));
    } catch {
      localStorage.removeItem(`views-order-${studyUID}`);
      setCustomOrder([]);
    } finally {
      setIsSavingOrder(false);
      setOrderSaved(false);
      setIsReordering(false);
    }
  };

  const [appliedFilters, setAppliedFilters] = useState({
    filterMode: 'view' as 'view' | 'quality',
    selectedGroups: [] as string[], selectedViews: [] as string[],
    selectedQualityFlags: [] as any[], magicFilterActive: false,
  });

  useEffect(() => {
    const h = (e: MouseEvent) => { if (isSeriesDropdownOpen && !(e.target as HTMLElement).closest('.series-filter-container')) setSeriesDropdownOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [isSeriesDropdownOpen]);

  const uniqueGroups = useMemo(() => Array.from(new Set<string>(Object.values(view_groupe).map((i: any) => i.group))), []);
  const availableViews = useMemo(() => {
    const apiCodes: string[] = reportViews?.data?.views?.map((v: any) => v.view_code) || [];
    if (selectedGroups.length === 0) return Array.from(new Set([...apiCodes, ...Object.keys(view_groupe)]));
    const inGroup = Object.keys(view_groupe).filter(c => selectedGroups.includes(view_groupe[c].group));
    return Array.from(new Set([...inGroup, ...apiCodes.filter(c => inGroup.includes(c))]));
  }, [selectedGroups, reportViews]);

  const availableQualityFlags = useMemo(() => {
    const codes = selectedViews.length > 0 ? selectedViews : selectedGroups.length > 0 ? Object.keys(view_groupe).filter(c => selectedGroups.includes(view_groupe[c].group)) : Object.keys(QUALITY_FLAGS);
    const m = new Map<string, any>();
    codes.forEach(c => { if (QUALITY_FLAGS[c]) (QUALITY_FLAGS[c] as any[]).forEach((f: any) => !m.has(f.code) && m.set(f.code, f)); });
    return Array.from(m.values());
  }, [selectedGroups, selectedViews]);

  const filteredViews = useMemo(() => {
    const apiViews: any[] = reportViews?.data?.views || [];
    const staticCodes = new Set(Object.keys(view_groupe));

    const allViews = Object.keys(view_groupe).map(viewCode => {
      const ex = apiViews.find((v: any) => v.view_code === viewCode);
      const pc = getPlaneConfig(viewCode);
      return {
        view_code: viewCode,
        display_name: pc?.displayName || viewCode.replace(/VIEW__|_/g, ' '),
        exists: !!ex, quality_flags: ex?.quality_flags || [],
        has_ai: ex?.has_ai ?? false, has_user: ex?.has_user ?? false, has_ai_filtered: ex?.has_ai_filtered ?? false,
        frame_count: ex?.frame_count ?? 0, first_frame_image_url: ex?.first_frame_image_url ?? null,
        quality_score: ex?.quality_score ?? null,
        quality_criteria_total: ex?.quality_criteria_total ?? 0,
        quality_criteria_met: ex?.quality_criteria_met ?? 0,
        ...(ex || {}),
      };
    });

    const extras = apiViews.filter((v: any) => !staticCodes.has(v.view_code)).map((v: any) => ({
      view_code: v.view_code, display_name: v.display_name || v.view_code.replace(/VIEW__|_/g, ' '),
      exists: true, quality_flags: v.quality_flags || [],
      has_ai: v.has_ai ?? false, has_user: v.has_user ?? false, has_ai_filtered: v.has_ai_filtered ?? false,
      frame_count: v.frame_count ?? 0, first_frame_image_url: v.first_frame_image_url ?? null,
      quality_score: v.quality_score ?? null, quality_criteria_total: v.quality_criteria_total ?? 0, quality_criteria_met: v.quality_criteria_met ?? 0,
      ...v,
    }));

    const combined = [...allViews, ...extras];
    const filtered = combined.filter((v: any) => {
      const vg = view_groupe[v.view_code];
      if (appliedFilters.selectedGroups.length > 0 && !vg) return false;
      const matchGroup = appliedFilters.selectedGroups.length === 0 || (vg && appliedFilters.selectedGroups.includes(vg.group));
      if (!matchGroup) return false;
      if (appliedFilters.magicFilterActive) return v.exists;
      if (appliedFilters.filterMode === 'view') return appliedFilters.selectedViews.length === 0 || appliedFilters.selectedViews.includes(v.view_code);
      if (appliedFilters.selectedQualityFlags.length === 0) return true;
      return appliedFilters.selectedQualityFlags.some(f => v.quality_flags?.includes(f.code));
    });

    return filtered.sort((a, b) => {
      if (a.exists !== b.exists) return a.exists ? -1 : 1;
      if (!a.exists) return 0;
      const score = (v: any) => v.has_user ? 3 : v.has_ai ? 2 : v.has_ai_filtered ? 1 : 0;
      const sd = score(b) - score(a);
      if (sd !== 0) return sd;
      return (b.quality_score ?? -1) - (a.quality_score ?? -1);
    });
  }, [appliedFilters, reportViews]);

  // Apply custom order on top of filteredViews (only when a saved order exists)
  const orderedViews = useMemo(() => {
    if (customOrder.length === 0) return filteredViews;
    const byCode = new Map(filteredViews.map(v => [v.view_code, v]));
    const ordered: any[] = [];
    customOrder.forEach(code => { if (byCode.has(code)) { ordered.push(byCode.get(code)); byCode.delete(code); } });
    // Append any new views not yet in custom order (e.g. newly detected)
    byCode.forEach(v => ordered.push(v));
    return ordered;
  }, [filteredViews, customOrder]);

  const hasUnapplied = useMemo(() => JSON.stringify({ filterMode, selectedGroups, selectedViews, selectedQualityFlags, magicFilterActive }) !== JSON.stringify({ filterMode: appliedFilters.filterMode, selectedGroups: appliedFilters.selectedGroups, selectedViews: appliedFilters.selectedViews, selectedQualityFlags: appliedFilters.selectedQualityFlags, magicFilterActive: appliedFilters.magicFilterActive }), [filterMode, selectedGroups, selectedViews, selectedQualityFlags, magicFilterActive, appliedFilters]);

  const applyFilters = () => setAppliedFilters({ filterMode, selectedGroups, selectedViews, selectedQualityFlags, magicFilterActive });
  const applyMagic = () => { setMagicFilterActive(true); setAppliedFilters({ filterMode: 'view', selectedGroups, selectedViews, selectedQualityFlags: [], magicFilterActive: true }); };
  const clearFilters = () => { setSelectedGroups([]); setSelectedViews([]); setSelectedQualityFlags([]); setMagicFilterActive(false); setAppliedFilters({ filterMode: 'view', selectedGroups: [], selectedViews: [], selectedQualityFlags: [], magicFilterActive: false }); };

  const handleViewSelect = (view: any, initialFrameId?: string) => {
    if (!view.exists) return;
    const existOnly = orderedViews.filter(v => v.exists);
    onViewSelect(view, existOnly, existOnly.findIndex(v => v.view_code === view.view_code), appliedFilters, initialFrameId);
  };

  const { t } = useTranslation(['MedicalViewsGrid', 'GeneralViewer']);

  const stats = useMemo(() => {
    const ex = filteredViews.filter(v => v.exists);
    return { total: filteredViews.length, existing: ex.length, user: ex.filter(v => v.has_user).length, ai: ex.filter(v => v.has_ai && !v.has_user).length, lowQa: ex.filter(v => v.has_ai_filtered && !v.has_ai && !v.has_user).length };
  }, [filteredViews]);

  const getSeriesBadge = (t: string) => ({ OB: 'bg-purple-100 text-purple-700', BLIND: 'bg-blue-100 text-blue-700', GYN: 'bg-pink-100 text-pink-700' })[t] || 'bg-gray-100 text-gray-700';

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900 min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Stats */}
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                {t('stats.viewsDetected', { detected: reportViews?.data?.total_views || 0, total: filteredViews.length })}
              </p>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {stats.user > 0 && <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><UserCheck size={10} /> {t('stats.confirmed', { count: stats.user })}</span>}
                {stats.ai > 0 && <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400"><Cpu size={10} /> {t('stats.ai', { count: stats.ai })}</span>}
                {stats.lowQa > 0 && <span className="flex items-center gap-1 text-xs text-amber-600"><AlertTriangle size={10} /> {t('stats.lowQuality', { count: stats.lowQa })}</span>}
                {appliedFilters.magicFilterActive && <span className="flex items-center gap-1 text-xs text-purple-600"><Sparkles size={10} /> {t('stats.magicActive')}</span>}
              </div>
            </div>
            {/* Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Grid/List toggle */}
              <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800 p-0.5 gap-0.5">
                <button onClick={() => setDisplayModeAndSave('grid')} className={`p-1.5 rounded-lg transition-colors ${displayMode === 'grid' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`} title={t('display.grid')}><LayoutGrid size={14} /></button>
                <button onClick={() => setDisplayModeAndSave('list')} className={`p-1.5 rounded-lg transition-colors ${displayMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`} title={t('display.list')}><LayoutList size={14} /></button>
              </div>
              {studyUID && (
                <ViewerToolCluster
                  studyUID={studyUID}
                  variant="light"
                  hideDicom={!onOpenDicomMetadata}
                  onDicomClick={
                    onOpenDicomMetadata
                      ? () =>
                          onOpenDicomMetadata({
                            studyUID,
                            primarySeriesUid: dicomInstances[0]?.serie_instance_uid ?? null,
                            initialTab: 'study',
                            selectedInstance: null,
                          })
                      : undefined
                  }
                  dicomInstances={dicomInstances}
                  liveExport={null}
                />
              )}
              {/* Series filter */}
              <div className="relative series-filter-container z-50">
                <button onClick={() => setSeriesDropdownOpen(!isSeriesDropdownOpen)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                  <i className="ri-equalizer-line" />
                  <span>
                    {selectedSerieType?.length === 1 && selectedSerieType[0] === 'All'
                      ? t('series.allSeriesCount', { count: availableSeries?.length ?? 0 })
                      : t('series.selectedSeriesCount', { count: selectedSerieType.length })}
                  </span>
                  <ChevronDown size={12} className={`transition-transform ${isSeriesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSeriesDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-[9999]">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('series.selectTitle')}</span>
                      <div className="flex items-center gap-2">
                        {(selectedSerieType.length > 1 || (selectedSerieType.length === 1 && selectedSerieType[0] !== 'All')) && <button onClick={onClearAllSeries} className="text-xs text-blue-600 hover:underline">{t('actions.clear')}</button>}
                        <button onClick={() => setSeriesDropdownOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                      </div>
                    </div>
                    <div className="py-1 max-h-56 overflow-y-auto">
                      <button onClick={() => onSerieFilterChange('All')} className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedSerieType.includes('All') ? 'text-blue-600 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                        <div className={`w-4 h-4 border rounded flex items-center justify-center ${selectedSerieType.includes('All') ? 'bg-blue-600 border-blue-600' : 'border-gray-400'}`}>{selectedSerieType.includes('All') && <Check size={10} className="text-white" />}</div>
                        {t('series.allSeriesCount', { count: availableSeries.length })}
                      </button>
                      {availableSeries.map((s, i) => {
                        const sel = selectedSerieType.includes(s.serie_instance_uid);
                        return (
                          <button key={`${s.serie_instance_uid}-${i}`} onClick={() => onSerieFilterChange(s.serie_instance_uid)} className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${sel ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}>
                            <div className="flex items-center">
                              <div className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 mr-2 ${sel ? 'bg-blue-600 border-blue-600' : 'border-gray-400'}`}>{sel && <Check size={10} className="text-white" />}</div>
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium ${sel ? 'text-blue-700' : 'text-gray-800 dark:text-gray-200'}`}>{s.type} #{i + 1}</div>
                                <div className="text-gray-400 font-mono truncate">{s.serie_instance_uid.substring(0, 22)}…</div>
                              </div>
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getSeriesBadge(s.type)}`}>{s.type}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                <Filter size={12} /> {showFilters ? t('filters.hide') : t('filters.show')} {showFilters ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>

              {/* Reorder button */}
              <button
                onClick={() => { setIsReordering(r => !r); setOrderSaved(false); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors shadow-sm ${
                  isReordering
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                title={t('reorder.toggleTitle')}
              >
                <GripVertical size={12} /> {t('reorder.button')}
              </button>

              {/* Save / Reset order — visible when reordering */}
              {isReordering && (
                <>
                  <button
                    onClick={saveOrder}
                    disabled={isSavingOrder}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border shadow-sm transition-colors disabled:opacity-60 ${
                      orderSaved ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                    }`}
                    title={t('reorder.saveTitle')}
                  >
                    <Save size={12} />
                    {isSavingOrder ? t('reorder.saving') : orderSaved ? t('reorder.saved') : t('reorder.save')}
                  </button>
                  {customOrder.length > 0 && (
                    <button
                      onClick={resetOrder}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white dark:bg-slate-800 text-red-500 border border-red-200 hover:bg-red-50 transition-colors shadow-sm"
                      title={t('reorder.resetTitle')}
                    >
                      <RotateCcw size={12} /> {t('actions.reset')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {showFilters && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-800 dark:text-white">{t('filters.title')}</h2>
                <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><RotateCcw size={10} /> {t('actions.reset')}</button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                <CompactMultiSelect options={uniqueGroups} value={selectedGroups} onChange={setSelectedGroups} placeholder={t('filters.placeholders.allGroups')} label={t('filters.groups')} />
                <CompactMultiSelect options={availableViews} value={selectedViews} onChange={setSelectedViews} placeholder={t('filters.placeholders.allViews')} label={t('filters.views')} />
                <QualityFlagsManager options={availableQualityFlags} value={selectedQualityFlags} onChange={setSelectedQualityFlags} placeholder={t('filters.placeholders.allFlags')} label={t('filters.qualityFlags')} />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={applyMagic} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl shadow-sm hover:from-violet-600 hover:to-purple-600">
                  <Sparkles size={11} /> {t('filters.magic')}
                </button>
                <button onClick={applyFilters} disabled={!hasUnapplied} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl shadow-sm transition-colors ${hasUnapplied ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'}`}>
                  <Play size={11} /> {t('actions.apply')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reorder hint banner */}
      {isReordering && (
        <div className="flex-shrink-0 flex items-center gap-2 px-5 py-2 bg-violet-50 dark:bg-violet-900/20 border-b border-violet-200 dark:border-violet-800">
          <GripVertical size={13} className="text-violet-500 shrink-0" />
          <span className="text-xs text-violet-700 dark:text-violet-300">
            <strong>{t('reorder.hint.dragAnyCardBold')}</strong> {t('reorder.hint.rest')}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedViews.map(v => v.view_code)}
              strategy={displayMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
            >
              {displayMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
                  {orderedViews.map((view) => (
                    <SortableViewCard
                      key={view.view_code}
                      view={view}
                      viewGrouping={view_groupe[view.view_code]}
                      studyUID={studyUID}
                      dicomInstances={dicomInstances}
                      onViewSelect={handleViewSelect}
                      onAssign={handleAssign}
                      isMagicActive={appliedFilters.magicFilterActive}
                      isReordering={isReordering}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 m-4 overflow-hidden">
                  {orderedViews.map((view) => (
                    <SortableViewListRow
                      key={view.view_code}
                      view={view}
                      viewGrouping={view_groupe[view.view_code]}
                      studyUID={studyUID}
                      dicomInstances={dicomInstances}
                      onViewSelect={handleViewSelect}
                      onAssign={handleAssign}
                      isReordering={isReordering}
                    />
                  ))}
                  {orderedViews.length === 0 && (
                    <div className="flex items-center justify-center py-12 text-sm text-slate-400">{t('empty.noViewsMatch')}</div>
                  )}
                </div>
              )}
            </SortableContext>

            {/* Drag overlay — ghost card while dragging */}
            <DragOverlay>
              {activeDragId ? (() => {
                const dragView = orderedViews.find(v => v.view_code === activeDragId);
                if (!dragView) return null;
                return (
                  <div className="opacity-90 shadow-2xl rotate-1 scale-105 pointer-events-none">
                    {displayMode === 'grid' ? (
                      <ViewCard
                        view={dragView}
                        viewGrouping={view_groupe[dragView.view_code]}
                        onViewSelect={() => {}}
                        onAssign={() => {}}
                        isMagicActive={false}
                        studyUID={studyUID}
                        dicomInstances={dicomInstances}
                        isReordering
                      />
                    ) : (
                      <div className="bg-white dark:bg-slate-800 border border-violet-300 rounded-xl overflow-hidden">
                        <ViewListRow
                          view={dragView}
                          viewGrouping={view_groupe[dragView.view_code]}
                          studyUID={studyUID}
                          dicomInstances={dicomInstances}
                          onViewSelect={() => {}}
                          onAssign={() => {}}
                        />
                      </div>
                    )}
                  </div>
                );
              })() : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {assigningView && studyUID && (
        <AssignToViewModal
          view={assigningView}
          studyUID={studyUID}
          onClose={() => setAssigningView(null)}
          onSuccess={() => { setAssigningView(null); onViewDataChange?.(); }}
        />
      )}
    </div>
  );
};

export default MedicalViewsGrid;